import { createAsyncThunk } from "@reduxjs/toolkit";
import { get, post, patch, del } from "../../Services/ApiBase";
import { API_ENDPOINTS } from "../../Services/ApiEndPoints";
import { getApiErrorMessage } from "../../utils/apiErrorMessage";

const CLIENT_MAPPING = "CLIENT_TO_ACCOUNTING";

/**
 * List client → accounting tag mappings — GET /tag-mappings?mappingType=CLIENT_TO_ACCOUNTING
 */
export const fetchTabMappings = createAsyncThunk(
  "tabMapping/fetchData",
  async (params = {}, { rejectWithValue }) => {
    try {
      const query = {
        mappingType: CLIENT_MAPPING,
        activeOnly: params.activeOnly === false ? false : true,
        ...params,
      };
      const response = await get(API_ENDPOINTS.TAG_MAPPINGS.GET_ALL, query);
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to fetch billing tag mappings"));
    }
  }
);

/**
 * Create mapping — POST /tag-mappings
 */
export const createTabMapping = createAsyncThunk(
  "tabMapping/create",
  async ({ sourceTagId, targetTagId }, { rejectWithValue }) => {
    try {
      const response = await post(
        API_ENDPOINTS.TAG_MAPPINGS.CREATE,
        {
          sourceTagId: String(sourceTagId),
          targetTagId: String(targetTagId),
          mappingType: CLIENT_MAPPING,
        },
        {},
        "json",
        { skipToast: true, suppressGlobalErrorModal: true }
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to create billing tag mapping"));
    }
  }
);

/**
 * PATCH /tag-mappings/:id — body: { sourceTagId, targetTagId } (and optionally isActive).
 */
export const updateTabMapping = createAsyncThunk(
  "tabMapping/update",
  async ({ id, sourceTagId, targetTagId, isActive }, { rejectWithValue }) => {
    try {
      const body = {};
      if (sourceTagId != null && String(sourceTagId).trim() !== "") {
        body.sourceTagId = String(sourceTagId).trim();
      }
      if (targetTagId != null && String(targetTagId).trim() !== "") {
        body.targetTagId = String(targetTagId).trim();
      }
      if (isActive !== undefined && isActive !== null) {
        body.isActive = Boolean(isActive);
      }
      if (Object.keys(body).length === 0) {
        return rejectWithValue("Nothing to update");
      }
      const endpoint = API_ENDPOINTS.TAG_MAPPINGS.UPDATE(id);
      const response = await patch(
        endpoint,
        body,
        {},
        {},
        {
          skipToast: true,
          suppressGlobalErrorModal: true,
        }
      );
      return { id, data: response.data };
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to update billing tag mapping"));
    }
  }
);

/**
 * Delete mapping — DELETE /tag-mappings/:id
 */
export const deleteTabMapping = createAsyncThunk(
  "tabMapping/delete",
  async (id, { rejectWithValue }) => {
    try {
      await del(API_ENDPOINTS.TAG_MAPPINGS.DELETE(id), {}, { skipToast: true });
      return id;
    } catch (error) {
      return rejectWithValue(getApiErrorMessage(error, "Failed to delete billing tag mapping"));
    }
  }
);
